<script lang="ts">
import { useStore } from "@/utils/vuex-utils"
import { defineComponent, computed, watch, ref } from "vue"
import type { Device, DateRange } from "@/types/moj-elektro"
import { useI18n } from "@/utils/i18n-utils"
import { useRouter } from "@/utils/router-utils"

export default defineComponent({
    name: "MojElektroFilters",
    components: {
        GeneralDatePicker: () => import("@/components/shared/GeneralDatePicker.vue"),
    },
    setup() {
        const store = useStore()
        const i18n = useI18n()
        const highlightedInput = ref<Record<string, boolean>>({
            et: false,
            vt: false,
            mt: false,
            date: false,
        })
        const router = useRouter()

        const selectedDeviceId = computed<number | null>(
            () => store.getters["mojElektroFiltersModule/getSelectedDeviceId"],
        )
        const vt = computed<number | null>({
            get: () => store.getters["mojElektroFiltersModule/getVt"],
            set: (value) => store.dispatch("mojElektroFiltersModule/setVt", value),
        })
        const mt = computed<number | null>({
            get: () => store.getters["mojElektroFiltersModule/getMt"],
            set: (value) => store.dispatch("mojElektroFiltersModule/setMt", value),
        })
        const et = computed<number | null>({
            get: () => store.getters["mojElektroFiltersModule/getEt"],
            set: (value) => store.dispatch("mojElektroFiltersModule/setEt", value),
        })

        const devices = computed<Device[]>(() => store.getters["externalDevices/getAllDevices"])
        const isLoading = computed<boolean>(
            () =>
                store.getters["externalTokens/getIsLoading"] ||
                store.getters["externalDevices/getIsLoading"],
        )
        const deviceTarrifCount = computed<number | null>(() => {
            const device = devices.value.find((d) => d.id === selectedDeviceId.value)

            return device?.tariffsCount ?? null
        })

        const oneYearEarlyer = () => {
            const now = new Date()
            now.setFullYear(now.getFullYear() - 1)

            return now
        }

        const initialDateRange = ref<DateRange>({
            startDate: oneYearEarlyer(),
            endDate: new Date(),
        })

        const selectedDateRange = ref<DateRange>(initialDateRange.value)

        const handleDateRangeChange = (newDateRange: DateRange) => {
            selectedDateRange.value = newDateRange
        }

        const validateTariff = (tariffValue: number | null) => {
            return tariffValue !== null
        }

        const validationForm = computed(() => {
            const validation = {
                device: true,
                date: true,
                et: true,
                vt: true,
                mt: true,
            } as Record<string, boolean>

            if (!selectedDeviceId.value) {
                validation.device = false
            }

            if (!selectedDateRange.value.startDate || !selectedDateRange.value.endDate) {
                validation.date = false
            }

            if (deviceTarrifCount.value === 1) {
                validation.et = validateTariff(et.value)
            } else if (deviceTarrifCount.value === 2 || deviceTarrifCount.value === null) {
                validation.vt = validateTariff(vt.value)
                validation.mt = validateTariff(mt.value)
            }

            return validation
        })

        const isFormValid = computed(() => {
            return !Object.keys(validationForm.value).find((key) => !validationForm.value[key])
        })

        const isInputHighlighted = (type: string) => {
            return !validationForm.value[type] && highlightedInput.value[type]
        }

        const handleDeviceChange = (event: Event) => {
            const select = event.target as HTMLSelectElement
            const deviceId = parseInt(select.value)

            store.dispatch("mojElektroFiltersModule/setSelectedDeviceId", deviceId)
        }

        const onInputFocus = (type: string) => {
            store.commit("sidebar/setInputFocused", true)
            highlightedInput.value[type] = false
        }

        const onInputBlur = (type: string) => {
            highlightedInput.value[type] = true
            // store.commit("sidebar/setInputFocused", false)
        }

        const onKeydown = (event: KeyboardEvent) => {
            const excludedKeys = ["-"]
            const keyIsExcluded = excludedKeys.includes(event.key)

            if (keyIsExcluded) {
                event.preventDefault()
            }

            return !keyIsExcluded
        }

        watch(
            devices,
            (newDevices) => {
                if (newDevices.length > 0 && !selectedDeviceId.value) {
                    // Handle default selection if needed
                    // store.dispatch("mojElektroFiltersModule/setSelectedDeviceId", 2)
                    // onProcessClick()
                }
            },
            { immediate: true },
        )

        const isOnMobile = computed(() => store.state.appLayoutModule.isMobile)
        const isOnLandscapeMobile = computed(() => store.state.appLayoutModule.isLandscapeMobile)

        const onProcessClick = () => {
            store.dispatch("mojElektroFiltersModule/setDateRange", selectedDateRange.value)
            store.dispatch("mojElektroCalculationsModule/setShowPlaceholder", false)

            store
                .dispatch(
                    "mojElektroCalculationsModule/fetchData",
                    i18n.t("moj_elektro.calculations.error_fetching"),
                )
                .then(() => {
                    if (isOnMobile.value && !isOnLandscapeMobile.value) {
                        store.dispatch("sidebar/setSidebarVisibility", false)
                    }

                    router.push({
                        path: "/external/integrations/moj-elektro/calculations",
                    })
                })
                .catch((err) => {
                    console.error("Fetch failed:", err)
                })
        }

        return {
            devices,
            selectedDeviceId,
            vt,
            mt,
            et,
            isLoading,
            handleDeviceChange,
            initialDateRange,
            selectedDateRange,
            handleDateRangeChange,
            deviceTarrifCount,
            onProcessClick,
            validationForm,
            isFormValid,
            i18n,
            onInputBlur,
            onInputFocus,
            onKeydown,
            isInputHighlighted,
        }
    },
})
</script>

<template>
    <div class="filters-container overflow-auto d-flex flex-column justify-content-between">
        <div class="filters-form">
            <div class="form-group">
                <label for="device-select">{{ i18n.t("moj_elektro.filters.devices") }}</label>
                <select
                    id="device-select"
                    class="form-select"
                    :value="selectedDeviceId"
                    @change="handleDeviceChange"
                    :disabled="isLoading"
                    @click.stop
                >
                    <option value="">{{ i18n.t("moj_elektro.filters.select_device") }}</option>
                    <option v-for="device in devices" :key="device.id" :value="device.id">
                        EIMM: {{ device.eimm }}, GSRN: {{ device.gsrn?.substring(0, 6) }}...
                    </option>
                </select>
            </div>

            <div class="form-group" v-if="selectedDeviceId">
                <label>{{ i18n.t("moj_elektro.filters.date_range") }} *</label>
                <GeneralDatePicker
                    :initialDateRange="initialDateRange"
                    :maxRangeSpan="730"
                    @dateRangeChanged="handleDateRangeChange"
                    :class="{ 'border-danger': !validationForm.date }"
                />
            </div>

            <template v-if="deviceTarrifCount === 1 && selectedDeviceId">
                <div class="form-group">
                    <label>ET [EUR/kWh] *</label>
                    <input
                        type="number"
                        min="0"
                        v-model.number="et"
                        class="form-control"
                        :class="{ 'border-danger': isInputHighlighted('et') }"
                        @blur="onInputBlur('et')"
                        @focus="onInputFocus('et')"
                        @keydown="onKeydown"
                    />
                </div>
            </template>

            <template
                v-if="(deviceTarrifCount === 2 || deviceTarrifCount === null) && selectedDeviceId"
            >
                <div class="form-group">
                    <label>VT [EUR/kWh] *</label>
                    <input
                        type="number"
                        min="0"
                        v-model.number="vt"
                        class="form-control"
                        :class="{ 'border-danger': isInputHighlighted('vt') }"
                        @blur="onInputBlur('vt')"
                        @focus="onInputFocus('vt')"
                        @keydown="onKeydown"
                    />
                </div>

                <div class="form-group">
                    <label>MT [EUR/kWh] *</label>
                    <input
                        type="number"
                        min="0"
                        v-model.number="mt"
                        class="form-control"
                        :class="{ 'border-danger': isInputHighlighted('mt') }"
                        @blur="onInputBlur('mt')"
                        @focus="onInputFocus('mt')"
                        @keydown="onKeydown"
                    />
                </div>
            </template>
        </div>
        <div class="w-100 d-flex justify-content-end">
            <button class="btn btn-primary" @click="onProcessClick" :disabled="!isFormValid">
                {{ i18n.t("moj_elektro.filters.process") }}
            </button>
        </div>
    </div>
</template>

<style lang="scss">
.filters-container {
    height: 100%;
    padding: 0.5rem;

    .filters-form {
        display: flex;
        flex-direction: column;
    }

    .form-group {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;

        .datepicker-container.border-danger input {
            border: 1px solid red !important;
        }

        input {
            border: 1px solid var(--theme-auxiliary) !important;
            border-radius: 0.5rem;

            &.border-danger {
                border: 1px solid red !important;
            }
        }
    }

    .form-select {
        width: 100%;
        padding: 0.375rem 0.75rem;
        font-size: 1rem;
        line-height: 1.5;
        border: 1px solid #ced4da;
        border-radius: 0.5rem;
        background-color: #fff;
    }

    .form-select:disabled {
        background-color: #e9ecef;
        cursor: not-allowed;
    }

    option {
        background-color: var(--theme-primary) !important;
    }
}
</style>
